/* Original artwork for the blog, drawn with plain elements rather than image
   files. Two reasons: nothing extra to download, and it recolours itself if the
   b2 tokens ever change. Each piece is a pale indigo panel with a small chat
   scene on it, so the blog reads as part of the product rather than stock art. */

function Panel({ children, className = "" }) {
  return (
    <div
      className={`relative w-full h-full overflow-hidden bg-b2-wash ${className}`}
      aria-hidden="true"
    >
      {/* soft shapes for depth, kept very low contrast */}
      <span className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/50" />
      <span className="absolute -left-12 bottom-[-3rem] w-44 h-44 rounded-full bg-white/35" />
      <div className="relative w-full h-full">{children}</div>
    </div>
  );
}

function Ask({ children, className = "" }) {
  return (
    <div
      className={`inline-block max-w-full bg-white rounded-[12px] rounded-bl-[4px]
                  px-3.5 py-2 text-[12.5px] leading-snug text-b2-ink
                  shadow-[0_1px_3px_rgba(16,24,40,0.07)] ${className}`}
    >
      {children}
    </div>
  );
}

function Reply({ children, className = "" }) {
  return (
    <div
      className={`inline-block max-w-full bg-b2-indigo rounded-[12px] rounded-br-[4px]
                  px-3.5 py-2 text-[12.5px] leading-snug text-white
                  shadow-[0_1px_3px_rgba(16,24,40,0.10)] ${className}`}
    >
      {children}
    </div>
  );
}

function Mark() {
  return (
    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white shadow-[0_1px_3px_rgba(16,24,40,0.10)]">
      <span className="flex gap-[3px]">
        <i className="w-[3px] h-[3px] rounded-full bg-b2-indigo" />
        <i className="w-[3px] h-[3px] rounded-full bg-b2-indigo" />
        <i className="w-[3px] h-[3px] rounded-full bg-b2-indigo" />
      </span>
    </span>
  );
}

/* Repeated questions, one calm answer. */
function Repeat() {
  return (
    <Panel>
      <div className="absolute inset-0 p-5 flex flex-col justify-center gap-2">
        <Ask className="self-start">Ada stok lagi?</Ask>
        <Ask className="self-start ml-4">Berapa harganya?</Ask>
        <Ask className="self-start ml-8">Boleh pos ke Sabah?</Ask>
        <div className="flex items-end gap-2 mt-1">
          <Mark />
          <Reply>Boleh! Kami pos ke seluruh Malaysia.</Reply>
        </div>
      </div>
    </Panel>
  );
}

/* Two languages meeting in the middle. */
function Language() {
  return (
    <Panel>
      <div className="absolute inset-0 flex items-center justify-center gap-3 px-5">
        <div className="bg-white rounded-[16px] rounded-bl-[5px] px-5 py-3
                        shadow-[0_2px_6px_rgba(16,24,40,0.08)]">
          <span className="text-[19px] font-bold tracking-[-0.02em] text-b2-ink">Salam</span>
        </div>
        <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0 text-b2-indigo" fill="currentColor">
          <path d="M12 2l1.8 5.4L19 9l-5.2 1.6L12 16l-1.8-5.4L5 9l5.2-1.6L12 2z" />
        </svg>
        <div className="bg-b2-indigo rounded-[16px] rounded-br-[5px] px-5 py-3
                        shadow-[0_2px_6px_rgba(16,24,40,0.10)]">
          <span className="text-[19px] font-bold tracking-[-0.02em] text-white">Hello</span>
        </div>
      </div>
    </Panel>
  );
}

/* What the assistant should know, as a ticked list. */
function Checklist() {
  const items = ["Produk & perkhidmatan", "Soalan lazim", "Polisi syarikat", "Gaya jawapan"];
  return (
    <Panel>
      <div className="absolute inset-0 p-5 flex items-center">
        <div className="w-full bg-white rounded-[14px] p-4 shadow-[0_2px_8px_rgba(16,24,40,0.08)]">
          <p className="text-[12px] font-semibold text-b2-ink mb-2.5">Maklumat penting</p>
          <ul className="space-y-2">
            {items.map((t) => (
              <li key={t} className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-[4px] bg-b2-indigo shrink-0">
                  <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 text-white" fill="none"
                       stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2.5 6.2l2.3 2.3L9.5 3.8" />
                  </svg>
                </span>
                <span className="text-[12px] text-b2-inksoft">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Panel>
  );
}

/* A question turning into a useful answer. Used for the featured post. */
function Guide() {
  return (
    <Panel>
      <div className="absolute inset-0 p-6 flex flex-col justify-center gap-3">
        <div className="bg-white/80 rounded-[14px] p-3.5 max-w-[62%] shadow-[0_1px_4px_rgba(16,24,40,0.06)]">
          <p className="text-[11px] font-semibold text-b2-faint mb-2">Soalan pelanggan</p>
          <div className="space-y-1.5">
            <Ask className="block">Berapa lama penghantaran?</Ask>
            <Ask className="block">Bagaimana cara membuat pembayaran?</Ask>
          </div>
        </div>
        <div className="self-end bg-white rounded-[14px] p-3.5 max-w-[72%] shadow-[0_2px_8px_rgba(16,24,40,0.09)]">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold text-b2-indigo mb-2">
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor">
              <path d="M12 2l1.8 5.4L19 9l-5.2 1.6L12 16l-1.8-5.4L5 9l5.2-1.6L12 2z" />
            </svg>
            Jawapan yang membantu
          </p>
          <p className="text-[12.5px] leading-relaxed text-b2-ink">
            Penghantaran biasanya 3 hingga 5 hari bekerja. Anda boleh membayar melalui FPX,
            kad debit atau e-dompet.
          </p>
        </div>
      </div>
    </Panel>
  );
}

const ART = { guide: Guide, repeat: Repeat, language: Language, checklist: Checklist };

export default function BlogArt({ name, className = "" }) {
  const Piece = ART[name] || Guide;
  return (
    <div className={className}>
      <Piece />
    </div>
  );
}
